import { FC, useMemo } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';
import useSWR from 'swr';

import { CreateTournamentButton } from './createTournamentButton/CreateTournamentButton';
import { ScheduleBalanceDistributionButton } from './scheduleBalanceDistributionButton/ScheduleBalanceDistributionButton';
import { TournamentActionsColumn } from './tournamentsTableColumns/tournamentActionsColumn/TournamentActionsColumn';
import { TournamentActiveColumn } from './tournamentsTableColumns/tournamentActiveColumn/TournamentActiveColumn';
import { TournamentNameColumn } from './tournamentsTableColumns/tournamentNameColumn/TournamentNameColumn';
import { TournamentProvidersColumn } from './tournamentsTableColumns/tournamentProvidersColumn/TournamentProvidersColumn';

import css from './TournamentsPage.module.css';

import { tournamentsService } from 'v2/api/tournamentsService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { Loader } from 'v2/components/loader/Loader';
import { PageLayout } from 'v2/components/pageLayout/PageLayout';
import { PageTitle } from 'v2/components/pageLayout/pageTitle/PageTitle';
import { usePagination } from 'v2/hooks/usePagination';
import { TableColumn } from 'v2/types';
import { TournamentModel } from 'v2/types/TournamentModel';
import { formatNumber } from 'v2/utils';

export const TournamentsPage: FC = () => {
  const pagination = usePagination();

  const { data, isLoading, mutate } = useSWR(
    [
      'getTournaments',
      pagination.page,
      pagination.pageSize,
      {
        isDeleted: false,
        isArchived: false,
      },
    ],
    tournamentsService.getTournaments,
    { keepPreviousData: true }
  );

  const columns: TableColumn<TournamentModel>[] = useMemo(
    () => [
      {
        key: 'isActive',
        label: 'Active',
        align: 'center',
        style: { width: '5%' },
        render: (row) => <TournamentActiveColumn tournament={row} onRevalidate={mutate} />,
      },
      {
        key: 'name',
        label: 'Tournament',
        style: { width: '15%' },
        render: (row) => <TournamentNameColumn tournament={row} />,
      },
      {
        key: 'providerNames',
        label: 'Providers',
        style: { width: '15%' },
        render: (row) => <TournamentProvidersColumn tournament={row} />,
      },
      { key: 'balance', label: 'Balance', render: (row) => formatNumber(row.balance) },
      { key: 'prizeFund', label: 'Prize Fund', render: (row) => formatNumber(row.prizeFund) },
      {
        key: 'startDate',
        label: 'Start Date',
        ellipsis: true,
        render: (row) => dayjs(row.startDate).format('DD.MM.YYYY HH:mm'),
      },
      {
        key: 'endDate',
        label: 'End Date',
        ellipsis: true,
        render: (row) => dayjs(row.endDate).format('DD.MM.YYYY HH:mm'),
      },
      { key: 'actions', render: (row) => <TournamentActionsColumn tournament={row} onRevalidate={mutate} /> },
    ],
    [mutate]
  );

  return (
    <PageLayout className={css.tournamentsPage} overflow="hidden">
      <PageTitle
        action={
          <div className={clsx('form-group', css.tournamentsPageActions)}>
            <CreateTournamentButton onSuccess={mutate} />

            <ScheduleBalanceDistributionButton />
          </div>
        }
      >
        Tournaments Settings
      </PageTitle>

      {isLoading ? (
        <Loader />
      ) : (
        <DataTable
          columns={columns}
          data={data?.result}
          page={pagination.page}
          total={data?.total}
          pageSize={pagination.pageSize}
          onPageChange={pagination.onPageChange}
          withPagination
        />
      )}
    </PageLayout>
  );
};
